import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

function Messages() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // 从URL参数获取要联系的人的信息
  const targetUserId = searchParams.get('to');
  const targetName = searchParams.get('name');
  const relatedJobId = searchParams.get('jobId');
  const relatedCandidateId = searchParams.get('candidateId');
  const prefillSubject = searchParams.get('subject');

  // 新对话模式
  const [isNewChat, setIsNewChat] = useState(false);
  const [newChatPrefill, setNewChatPrefill] = useState('');

  useEffect(() => {
    if (!user) return;
    fetchContacts();
  }, [user]);

  useEffect(() => {
    if (targetUserId && user) {
      setActiveContact({ contactId: targetUserId, contactName: targetName || '用户' });
      setIsNewChat(true);
      if (prefillSubject) {
        setNewChatPrefill(`关于「${prefillSubject}」：\n\n`);
        setNewMessage(`关于「${prefillSubject}」：\n\n`);
      }
      fetchConversation(targetUserId);
    }
  }, [targetUserId, user]);

  const fetchContacts = async () => {
    try {
      const res = await api.get('/messages/contacts');
      setContacts(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchConversation = async (contactId) => {
    try {
      const res = await api.get(`/messages/conversation/${contactId}`);
      setConversation(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); }
  };

  const handleSelectContact = (contact) => {
    setActiveContact(contact);
    setIsNewChat(false);
    setNewMessage('');
    fetchConversation(contact.contactId);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeContact) return;
    setSending(true);
    try {
      await api.post('/messages', {
        toUserId: activeContact.contactId,
        content: newMessage,
        relatedJobId: relatedJobId || undefined,
        relatedCandidateId: relatedCandidateId || undefined,
      });
      setNewMessage('');
      setIsNewChat(false);
      fetchConversation(activeContact.contactId);
      fetchContacts();
    } catch (err) {
      alert(err.response?.data?.error || '发送失败');
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 60, textAlign: 'center' }}>
        <p>请先 <Link to="/login" style={{ color: 'var(--primary)' }}>登录</Link> 后查看消息</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 20, paddingBottom: 60 }}>
      <div className="page-header">
        <h1 className="page-title">站内消息</h1>
        <p className="page-subtitle">与岗位发布者和候选人直接沟通</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, minHeight: 500 }}>
        {/* Contact List */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: '0.9rem' }}>
            联系人
          </div>
          {loading ? (
            <div style={{ padding: 20, color: 'var(--text-light)', textAlign: 'center', fontSize: '0.85rem' }}>加载中...</div>
          ) : contacts.length === 0 && !isNewChat ? (
            <div style={{ padding: 20, color: 'var(--text-light)', textAlign: 'center', fontSize: '0.85rem' }}>
              暂无消息记录
            </div>
          ) : (
            <div style={{ maxHeight: 440, overflowY: 'auto' }}>
              {isNewChat && targetUserId && !contacts.find(c => c.contactId === targetUserId) && (
                <div
                  style={{
                    padding: '12px 16px', cursor: 'pointer',
                    background: activeContact?.contactId === targetUserId ? 'var(--primary-light)' : 'transparent',
                    borderBottom: '1px solid var(--border-light)',
                  }}
                  onClick={() => { setActiveContact({ contactId: targetUserId, contactName: targetName }); fetchConversation(targetUserId); }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="comment-avatar">{(targetName || '?')[0].toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{targetName || '用户'}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>新对话</div>
                    </div>
                  </div>
                </div>
              )}
              {contacts.map(c => (
                <div
                  key={c.contactId}
                  style={{
                    padding: '12px 16px', cursor: 'pointer',
                    background: activeContact?.contactId === c.contactId ? 'var(--primary-light)' : 'transparent',
                    borderBottom: '1px solid var(--border-light)',
                    transition: 'var(--transition)',
                  }}
                  onClick={() => handleSelectContact(c)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="comment-avatar">{(c.contactName || c.contactUsername || '?')[0].toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{c.contactName || c.contactUsername}</span>
                        {c.unreadCount > 0 && (
                          <span style={{
                            background: 'var(--primary)', color: 'white', borderRadius: '50%',
                            width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', flexShrink: 0,
                          }}>{c.unreadCount}</span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.78rem', color: 'var(--text-light)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{c.lastMessage}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conversation */}
        <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          {!activeContact ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
              选择一个联系人开始对话
            </div>
          ) : (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
                {activeContact.contactName || activeContact.contactUsername || '对话'}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', maxHeight: 360 }}>
                {conversation.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--text-light)', padding: 40, fontSize: '0.9rem' }}>
                    开始你们的对话吧
                  </div>
                )}
                {conversation.map(msg => {
                  const isMe = msg.fromUserId === user.id;
                  return (
                    <div key={msg.id} style={{
                      display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 12,
                    }}>
                      <div style={{
                        maxWidth: '70%',
                        padding: '10px 14px',
                        borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                        background: isMe ? 'var(--primary)' : 'var(--border-light)',
                        color: isMe ? 'white' : 'var(--text)',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                      }}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                        <div style={{
                          fontSize: '0.7rem', marginTop: 4,
                          color: isMe ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
                          textAlign: 'right',
                        }}>
                          {new Date(msg.createdAt).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="输入消息... (Enter发送，Shift+Enter换行)"
                  style={{
                    flex: 1, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                    fontSize: '0.9rem', fontFamily: 'inherit', resize: 'none', outline: 'none', minHeight: 44, maxHeight: 100,
                  }}
                  rows={1}
                />
                <button className="btn btn-primary" onClick={handleSend} disabled={sending || !newMessage.trim()}>
                  {sending ? '...' : '发送'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Messages;
